import { cn } from '@/lib/utils'

const SIZES = {
  // track (with 2px padding), knob, knob travel, and the same while pressed (the knob stretches
  // toward the side it is heading for, so a press already shows where it will land).
  sm: { track: 'h-5 w-9', knob: 'h-4 w-4 group-active:w-5', on: 'translate-x-4 group-active:translate-x-3' },
  md: { track: 'h-6 w-11', knob: 'h-5 w-5 group-active:w-6', on: 'translate-x-5 group-active:translate-x-4' },
  lg: { track: 'h-7 w-[3.25rem]', knob: 'h-6 w-6 group-active:w-7', on: 'translate-x-6 group-active:translate-x-5' },
} as const

/**
 * An on/off setting. Navy when on, like the primary button; the knob slides over with a slight
 * overshoot and the track fades to its colour. Reduced motion drops the slide, not the state.
 *
 * Put it inside a <label> with the setting's text (SettingToggle does), or give it aria-label.
 */
export function Switch({
  checked,
  onCheckedChange,
  disabled = false,
  size = 'md',
  className,
  ...aria
}: {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  disabled?: boolean
  size?: keyof typeof SIZES
  className?: string
  'aria-label'?: string
  'aria-describedby'?: string
}) {
  const s = SIZES[size]
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        'group relative inline-flex shrink-0 cursor-pointer items-center rounded-full p-0.5',
        'transition-colors duration-200 ease-out motion-reduce:transition-none',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-40',
        checked ? 'bg-brand-700 hover:bg-brand-800' : 'bg-[#B6C4DB] hover:bg-[#A3B3CE]',
        s.track,
        className,
      )}
      {...aria}
    >
      <span
        aria-hidden
        className={cn(
          'pointer-events-none block rounded-full bg-white shadow-[0_1px_2px_rgba(11,32,64,0.25),0_2px_6px_-1px_rgba(11,32,64,0.2)]',
          'transition-[transform,width] duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] motion-reduce:transition-none',
          s.knob,
          checked ? s.on : 'translate-x-0',
        )}
      />
    </button>
  )
}
